<template>
  <v-container fluid id="tract-bids">
    <DataTable
    :items="bidsForDataTable"
    :headers="headers"
    :actions="tableActions"
    :loading.sync="loading"
    :customCells="customColumns"
    htmlIdentifier="tract-bids"
    defaultSort="bidDate"
    sortDesc
    showPointerCursor
    @refresh="refreshBids"
    @create="openCreate"
    @click:row="openDetail($event.data)">
      <template #bidDate="{item}">
        <span>{{ formatDate(item.bidDate) }}</span>
      </template>
      <template #bidExpiration="{item}">
        <span>{{ formatDate(item.bidExpiration) }}</span>
        <Icon
        v-if="isBidExpired(item)"
        icon="mdi-timer-alert-outline"
        iconColor="error"
        :tooltipText="$t('bidExpired')"/>
      </template>
      <template #rfbStatus="{item}">
        <span>{{ rfbStatusLabel(item.rfbStatus) }}</span>
      </template>
      <template #outcome="{item}">
        <span v-if="item.outcome !== null && item.outcome !== undefined">{{ outcomeLabel(item.outcome) }}</span>
      </template>
      <template #actions="{item}">
        <Icon
        icon="mdi-pencil"
        iconColor="success"
        elementId="edit-bid"
        :tooltipText="$t('edit')"
        @icon-clicked="openEdit(item)"/>
        <Icon
        icon="mdi-content-copy"
        :iconColor="dueDiligenceBlocked ? 'grey' : 'primary'"
        elementId="duplicate-bid"
        :overrideClickable="dueDiligenceBlocked"
        :tooltipText="dueDiligenceBlocked ? $t('bidRequiresDueDiligenceCleared') : $t('copyBid')"
        @icon-clicked="openDuplicate(item)"/>
        <Icon
        icon="mdi-gavel"
        iconColor="primary"
        elementId="record-outcome"
        :tooltipText="$t('recordOutcome')"
        @icon-clicked="openOutcome(item)"/>
        <Icon
        icon="mdi-delete-forever"
        :iconColor="isWonBid(item) ? 'grey' : 'error'"
        elementId="delete-bid"
        :overrideClickable="isWonBid(item)"
        :tooltipText="isWonBid(item) ? $t('wonBidCannotBeDeleted') : $t('delete')"
        @icon-clicked="promptDelete(item)"/>
      </template>
    </DataTable>

    <BidWizard ref="bidWizard" @saved="onWizardSaved"/>
    <BidOutcomeDialog ref="bidOutcome" @saved="onOutcomeSaved"/>
    <BidDuplicateDialog ref="bidDuplicate" @saved="refreshBids"/>
    <BidDetailDialog ref="bidDetail" @edit="onDetailEdit"/>

    <v-dialog v-model="deleting" width="400" v-if="deleting">
      <ConfirmDelete
      :title="$t('bid')"
      @delete="confirmDelete"
      @cancel-delete="cancelDelete"/>
    </v-dialog>
  </v-container>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import BidHeaders from '@/headers/Bid.js'
import { Outcome, RfbStatus } from '@/utils/Enumerations.js'
import { utcToLocalDate } from '@/utils/DateFormatter.js'
import { isTractCleared } from '@/utils/dueDiligence/exemption.js'
import { isBidOpen } from '@/model/Bid.js'

export default {
  name: 'TractBids',

  props: {
    tractId: {
      type: Number,
      required: true
    },
    tract: {
      type: Object,
      required: true
    }
  },

  components: {
    DataTable: () => import('@/components/core/table/DataTable.vue'),
    Icon: () => import('@/components/helper/Icon.vue'),
    BidWizard: () => import('@/components/tract/BidWizard.vue'),
    BidOutcomeDialog: () => import('@/components/tract/BidOutcomeDialog.vue'),
    BidDuplicateDialog: () => import('@/components/bid/BidDuplicateDialog.vue'),
    BidDetailDialog: () => import('@/components/bid/BidDetailDialog.vue'),
    ConfirmDelete: () => import('@/components/helper/ConfirmDelete.vue')
  },

  data: () => ({
    bids: [],
    loading: false,
    deleting: false,
    deleteTarget: undefined
  }),

  computed: {
    ...mapGetters('account', ['allAccounts']),

    headers () {
      return BidHeaders.tractBidHeaders()
    },

    customColumns () {
      return [
        { slotName: 'bidDate', value: 'bidDate' },
        { slotName: 'bidExpiration', value: 'bidExpiration' },
        { slotName: 'rfbStatus', value: 'rfbStatus' },
        { slotName: 'outcome', value: 'outcome' },
        { slotName: 'actions', value: 'actions' }
      ]
    },

    tableActions () {
      return [
        {
          actionName: 'refresh',
          icon: 'mdi-refresh',
          text: this.$t('refresh')
        },
        {
          actionName: 'create',
          icon: 'mdi-plus',
          text: this.dueDiligenceBlocked ? this.$t('bidRequiresDueDiligenceCleared') : this.$t('createBid'),
          iconColor: this.dueDiligenceBlocked ? 'grey' : 'primary',
          id: 'create-bid',
          overrideClickable: this.dueDiligenceBlocked
        }
      ]
    },

    dueDiligenceBlocked () {
      return !isTractCleared(this.tract)
    },

    bidsForDataTable () {
      return this.bids.map(bid => ({
        ...bid,
        supplierAccountName: this.accountName(bid.supplierAccountId),
        destinationAccountName: this.accountName(bid.destinationAccountId)
      }))
    }
  },

  created () {
    if (!this.allAccounts.length) this.fetchAccounts()
    this.refreshBids()
  },

  methods: {
    ...mapActions('bid', ['fetchBids', 'deleteBid']),
    ...mapActions('account', ['fetchAccounts']),

    outcomeLabel: (x) => Outcome.fromInt(x),
    rfbStatusLabel: (x) => RfbStatus.fromInt(x),

    isBidExpired (bid) {
      return isBidOpen(bid) && !!bid.bidExpiration && new Date(bid.bidExpiration) < new Date()
    },

    formatDate (date) {
      return date ? utcToLocalDate(date) : this.$t('notAvailable')
    },

    accountName (accountId) {
      if (accountId === null || accountId === undefined) return this.$t('notAvailable')
      return this.allAccounts.find(a => a.accountId === accountId)?.name ?? accountId
    },

    async refreshBids () {
      this.loading = true
      try {
        this.bids = await this.fetchBids({ tractId: this.tractId })
      } finally {
        this.loading = false
      }
    },

    openCreate () {
      if (this.dueDiligenceBlocked) return
      this.$refs.bidWizard.open({ tractId: this.tractId, tract: this.tract })
    },

    openEdit (bid) {
      this.$refs.bidWizard.open({ tractId: this.tractId, tract: this.tract, bid })
    },

    openDetail (bid) {
      this.$refs.bidDetail.open(bid)
    },

    onDetailEdit (bid) {
      setTimeout(() => this.openEdit(bid), 0)
    },

    onWizardSaved () {
      this.refreshBids()
      this.$refs.bidDetail.refresh()
      this.$emit('outcome-saved')
    },

    openOutcome (bid) {
      this.$refs.bidOutcome.open(bid, this.tract)
    },

    openDuplicate (bid) {
      if (this.dueDiligenceBlocked) return
      this.$refs.bidDuplicate.open(bid, this.bids)
    },

    onOutcomeSaved () {
      this.refreshBids()
      this.$emit('outcome-saved')
    },

    isWonBid (bid) {
      return bid.outcome === Outcome.Won.value
    },

    promptDelete (bid) {
      if (this.isWonBid(bid)) return
      this.deleteTarget = bid
      this.deleting = true
    },

    cancelDelete () {
      this.deleting = false
      this.deleteTarget = undefined
    },

    async confirmDelete () {
      await this.deleteBid(this.deleteTarget.bidId)
      this.cancelDelete()
      this.refreshBids()
    }
  }
}
</script>
